import {
  Alert,
  Avatar,
  Button,
  Descriptions,
  Form,
  Space,
  Typography,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import View from "../controls/View";
import InModal from "../controls/InModal";
import DynamicForm from "../controls/DynamicForm";
import { Consultant } from "../repository/data.default";
import {
  getConsultant,
  updateUserProfile,
  addConsultant,
  deleteConsultant,
  getCompanyConsultants,
  addCompanyConsultants,
  deleteCompanyConsultants,
} from "../repository/data.repository";
import { getProblemMessage } from "../utils/http";
import { useSelector } from "../store/store";
import { authorizeSelectors } from "../store/authorize";
import useUserProfileCard from "../hooks/useUserProfileCard";
import UserProfileActions from "./user-card/UserProfileActions";
import UserActionDialogs from "./user-card/UserActionDialogs";
import UserCompanyAssignmentsPanel from "./user-card/UserCompanyAssignmentsPanel";
import UserMetricsPanel from "./user-card/UserMetricsPanel";
import AffiliateDocumentsPanel from "./user-card/AffiliateDocumentsPanel";
import AffiliateVerificationPanel from "./user-card/AffiliateVerificationPanel";
import {
  userIdentityFields,
  userContactFields,
  userAccountFields,
  affiliateProfessionalFields,
  affiliateServicesFields,
  affiliateAgreementsFields,
  affiliateAccessFields,
} from "../controls/userProfileFields";
import { FieldConfig, renderFields } from "../controls/DescriptionsFields";

export interface ConsultantViewProps {
  readonly open?: boolean;
  readonly mode: "add" | "view" | "delete";
  readonly uid?: string;
  readonly onFinish?: () => void;
  readonly onClose?: () => void;
  readonly initialValues?: Consultant;
}

const identityFields: FieldConfig[] = userIdentityFields();

function ConsultantView(props: ConsultantViewProps) {
  const permissions = useSelector(authorizeSelectors.permissions);
  const card = useUserProfileCard({
    uid: props.uid,
    open: props.open,
    getFn: getConsultant,
    updateFn: (input) => updateUserProfile(input as any),
    withHistory: true,
  });
  const { data, editor } = card;

  const [addForm] = Form.useForm();
  const addValues = Form.useWatch<any>([], addForm);
  const [valid, setValid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("Something went wrong, try again");

  const canEdit = permissions.can("PlatformConsultants", "create");

  useEffect(() => {
    if (props.open) {
      addForm.setFieldsValue(props.initialValues);
    } else {
      addForm.resetFields();
      setError(false);
      setSubmitting(false);
    }
  }, [props.open]);

  useEffect(() => {
    addForm
      .validateFields({ validateOnly: true })
      .then((value) => setValid(!!Object.values(value).length))
      .catch(() => setValid(false));
  }, [addForm, addValues, props.open]);

  function onSubmit(): void {
    setSubmitting(true);
    setError(false);
    if (props.mode === "add") {
      const { identifier, firstName, middleName, lastName, email, phoneNumber } =
        (addValues || {}) as any;
      addConsultant({ identifier, firstName, middleName, lastName, email, phoneNumber })
        .then(() => {
          setSubmitting(false);
          props.onFinish?.();
        })
        .catch((e: any) => {
          setErrorMessage(getProblemMessage(e));
          setSubmitting(false);
          setError(true);
        });
    } else if (props.mode === "delete") {
      deleteConsultant(props.uid!)
        .then(() => {
          setSubmitting(false);
          props.onFinish?.();
        })
        .catch((e: any) => {
          setErrorMessage(getProblemMessage(e));
          setSubmitting(false);
          setError(true);
        });
    }
  }

  const contactFields: FieldConfig[] = userContactFields(data, {
    editable: true,
    email: { onConfirm: canEdit ? () => card.onInputOpen("confirmEmail") : undefined },
    phone: { onConfirm: canEdit ? () => card.onInputOpen("confirmPhone") : undefined },
  });

  return (
    <>
      {props.mode === "view" && (
        <View
          open={props.open}
          loading={card.loading}
          title={"Consultant"}
          onBack={() => props.onClose?.()}
          actions={
            <UserProfileActions
              canEdit={canEdit}
              editor={editor}
              item={card.item}
              onInputOpen={card.onInputOpen}
              show={{ lock: true }}
            />
          }
        >
          <Form form={editor.form} component={false}>
            <div className="flex flex-col gap-4 p-4">
              <div className="flex flex-row items-center gap-4">
                <Avatar size={64} icon={<UserOutlined />} />
                <div className="flex flex-col">
                  <Typography.Title level={4} className="!mb-0">
                    {card.item?.userName}
                  </Typography.Title>
                  <Typography.Text type="secondary">Consultant</Typography.Text>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <Descriptions
                  title="Identity"
                  bordered
                  size="small"
                  column={2}
                  items={renderFields(identityFields, data, editor.editing)}
                />
                <Descriptions
                  title="Contact"
                  bordered
                  size="small"
                  column={1}
                  items={renderFields(contactFields, data, editor.editing)}
                />
              </div>

              {/* Admin brief 2026-11-03 — Affiliate profile sections. Four
                  dedicated blocks (Professional / Services / Agreements /
                  Access) so operators manage external professionals from one
                  place without jumping between pages. */}
              <Descriptions
                title="Professional Details"
                bordered
                size="small"
                column={2}
                items={renderFields(
                  affiliateProfessionalFields(data, { editable: canEdit }),
                  data,
                  editor.editing,
                )}
              />

              <Descriptions
                title="Services Offered"
                bordered
                size="small"
                column={2}
                items={renderFields(
                  affiliateServicesFields(data, { editable: canEdit }),
                  data,
                  editor.editing,
                )}
              />

              <Descriptions
                title="Agreements"
                bordered
                size="small"
                column={2}
                items={renderFields(
                  affiliateAgreementsFields(data, { editable: canEdit }),
                  data,
                  editor.editing,
                )}
              />

              <Descriptions
                title="Account & Access"
                bordered
                size="small"
                column={2}
                items={[
                  ...renderFields(
                    userAccountFields({ joinedDate: false, editable: true }),
                    data,
                    editor.editing,
                  ),
                  ...renderFields(
                    affiliateAccessFields(data, { editable: canEdit }),
                    data,
                    editor.editing,
                  ),
                ]}
              />

              <UserCompanyAssignmentsPanel
                uid={props.uid}
                open={props.open}
                loadFn={getCompanyConsultants}
                addFn={addCompanyConsultants}
                removeFn={deleteCompanyConsultants}
              />

              {/* Admin brief 2026-11-04 — verification workflow + docs. */}
              <AffiliateVerificationPanel
                uid={props.uid}
                open={!!props.open}
                status={data?.verificationStatus}
                verifiedAt={data?.verifiedAt}
                rejectedAt={data?.rejectedAt}
                rejectionReason={data?.rejectionReason}
                onChanged={() => card.reload?.()}
              />
              <AffiliateDocumentsPanel uid={props.uid} open={!!props.open} />

              <UserMetricsPanel history={card.history} loyaltyPoints={data?.loyaltyPoints} lastLoginAt={data?.lastLoginAt} createdOn={data?.createdOn} />
            </div>
          </Form>
        </View>
      )}

      {props.mode !== "view" && (
        <InModal
          open={props.open}
          size={props.mode === "delete" ? "small" : "medium"}
          title={(props.mode === "add" ? "Add" : "Delete") + " Consultant"}
          onCancel={() => props.onClose?.()}
          actions={
            <Space>
              <Button disabled={submitting} onClick={() => props.onClose?.()}>
                Cancel
              </Button>
              <Button
                type={"primary"}
                danger={props.mode === "delete"}
                loading={submitting}
                onClick={onSubmit}
                disabled={!valid && props.mode !== "delete"}
              >
                {props.mode === "add" ? "Add" : "Delete"}
              </Button>
            </Space>
          }
        >
          {props.mode === "add" && (
            <DynamicForm
              className="w-auto"
              form={addForm}
              schema={{
                groups: [
                  {
                    controls: [
                      {
                        type: "textbox",
                        name: "identifier",
                        label: "User Name",
                        rules: [{ required: true, message: "User Name is required" }],
                      },
                      { type: "textbox", name: "firstName", label: "First Name" },
                      { type: "textbox", name: "middleName", label: "Middle Name" },
                      { type: "textbox", name: "lastName", label: "Last Name" },
                      {
                        type: "textbox",
                        name: "email",
                        label: "Email",
                        rules: [{ type: "email", message: "Invalid email format" }],
                      },
                      { type: "textbox", name: "phoneNumber", label: "Phone Number" },
                    ],
                  },
                ],
              }}
            />
          )}
          {props.mode === "delete" && (
            <Typography.Paragraph>
              <Typography.Text>
                Continue deleting the{" "}
                <Typography.Text className="font-bold">{card.item?.userName}</Typography.Text>{" "}
                consultant?
              </Typography.Text>
            </Typography.Paragraph>
          )}
          {error && <Alert className="mb-2" message={errorMessage} type="error" />}
        </InModal>
      )}

      <UserActionDialogs
        item={card.item}
        inputMode={card.inputMode}
        inputOpen={card.inputOpen}
        onClose={card.onInputClose}
        onFinish={card.onInputFinish}
      />
    </>
  );
}

export default ConsultantView;
